/**
 * <Seo /> — per-route SEO head manager.
 *
 * Emits page-specific <title>, <meta description>, self-referencing <link
 * rel="canonical">, Open Graph + Twitter tags, and (optionally) a robots
 * directive. Rendered by react-helmet-async so the head reflects the
 * currently mounted route.
 *
 * IMPORTANT: our index.html still contains the homepage-baseline tags so
 * the FIRST paint from a naive crawler sees valid metadata. Helmet then
 * overrides them for the specific route once React hydrates. When
 * Googlebot renders JS (which it does), it will see the per-route tags.
 *
 * Static prerendering of each route to HTML is a possible follow-up
 * (would give crawlers the correct metadata without a JS render pass),
 * but it requires build-tool infrastructure changes and is out of scope
 * for this preview-only phase.
 */
import { Helmet } from "react-helmet-async";

const SITE_ORIGIN = "https://getpawlink.com";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/brand/social-og-1200x630.png`;

export default function Seo({
    title,
    description,
    path = "/",
    ogTitle,
    ogDescription,
    ogImage = DEFAULT_OG_IMAGE,
    noindex = false,
}) {
    const canonical = `${SITE_ORIGIN}${path === "/" ? "/" : path.replace(/\/$/, "")}`;
    const resolvedOgTitle = ogTitle || title;
    const resolvedOgDescription = ogDescription || description;
    const robots = noindex
        ? "noindex, follow"
        : "index, follow, max-image-preview:large, max-snippet:-1";

    return (
        <Helmet prioritizeSeoTags>
            <title>{title}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonical} />
            <meta name="robots" content={robots} />

            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="PawLink" />
            <meta property="og:url" content={canonical} />
            <meta property="og:title" content={resolvedOgTitle} />
            <meta property="og:description" content={resolvedOgDescription} />
            <meta property="og:image" content={ogImage} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={resolvedOgTitle} />
            <meta name="twitter:description" content={resolvedOgDescription} />
            <meta name="twitter:image" content={ogImage} />
        </Helmet>
    );
}
